import {Dispatch, SetStateAction} from 'react';
import {useNavigate} from 'react-router-dom';
import {BottomSheet} from 'react-spring-bottom-sheet';

import SpinnerLoading from '../../../../components/SpinnerLoading';
import useGetOrderOtpCode from '../../../../services/hookService/getOrderOtpCode/useGetOrderOtpCode';
import {
  PaymentMethodValues,
  TransactionTypesChoices,
} from '../../../shopping-basket/cash-payment/utils/helpers';
import useGetCashBackCards from '../../cashBack/api/getCashBackCards/useGetCashBackCards';
import ErrorBottomSheet from './ErrorBottomSheet';

import useGetCreditPayAvailableAmounts from '../../cashBack/api/getCreditPayAvailableAmounts/useGetCreditPayAvailableAmounts';
import useGetCreditPayAvailableOptions from '../../cashBack/api/getCreditPayAvailableOptions/useGetCashBackCardDetail';
import CreditDetailsStep from './CreditDetailsStep';
import CreditTypeStep from './CreditTypeStep';
import {useLendTechPayFlow} from './hooks/UseLendTechPayFlow';
import {PaymentMethodType, PayStep} from './LendTechPaySheetTypes';
import OtpStep from './OtpStep';
import PaymentMethodStep from './PaymentMethodStep';

// when the transition type is store order both the order and store order guid needs to be passed here
interface ILendTechPaySheetProps {
  isOpen: boolean;
  setIsOpen: Dispatch<SetStateAction<boolean>>;
  transactionType: TransactionTypesChoices;
  orderGuid?: string;
  storeOrderGuid?: string;
}

const LendTechPaySheet = ({
  isOpen,
  setIsOpen,
  transactionType,
  orderGuid,
  storeOrderGuid,
}: ILendTechPaySheetProps) => {
  const navigate = useNavigate();
  const flow = useLendTechPayFlow({isOpen, setIsOpen});
  const {step, paymentType, finalPaymentType} = flow;

  const {
    data: creditPayAvailableOptions,
    isLoading: isCreditPayAvailableOptionsLoading,
  } = useGetCreditPayAvailableOptions(isOpen);

  const {
    data: creditPayAvailableAmounts,
    isLoading: isCreditPayAvailableAmountLoading,
  } = useGetCreditPayAvailableAmounts(step === PayStep.CreditType);

  const {totalCashBackActiveCredit, isLoading: isCashBackCardsLoading} =
    useGetCashBackCards(step === PayStep.CreditType);

  const isCreditPayEnabled =
    (creditPayAvailableOptions?.totalCreditOptions ?? 0) > 0;

  const {refetch: requestOtp, isFetching: isRequestingOtp} = useGetOrderOtpCode(
    () => flow.goTo(PayStep.Otp),
  );

  const goToCashPayment = (type?: PaymentMethodValues) => {
    setIsOpen(false);
    navigate(`/cash-payment/${orderGuid}/${storeOrderGuid ?? ''}`, {
      state: {transactionType, paymentType: type},
    });
  };

  const goToMyOrders = () => {
    navigate('/my-orders', {state: {isOrderRegisterSuccess: true}});
  };

  const handleSelectMethod = (method: PaymentMethodType) => {
    if (method === 'cash-payment') {
      goToCashPayment(PaymentMethodValues.CASH_PAYMENT);
      return;
    }

    if (isCreditPayEnabled) {
      // if there is only one method pre select it and skip creditType step
      if (creditPayAvailableOptions?.totalCreditOptions === 1) {
        if (creditPayAvailableOptions.has_cashback) {
          flow.setPaymentType(PaymentMethodValues.CASHBACK_PAYMENT);
        } else if (creditPayAvailableOptions.has_credit) {
          flow.setPaymentType(PaymentMethodValues.CREDIT_PAYMENT);
        }
        // TODO : ADD BNPL
        // else if (creditPayAvailableOptions?.has_bnpl) {
        //   flow.setPaymentType(PaymentMethodValues.BNPL_PAYMENT);
        // }

        flow.goTo(PayStep.CreditDetails);
      } else {
        flow.goTo(PayStep.CreditType);
      }
    } else {
      flow.setPaymentType(PaymentMethodValues.CREDIT_PAYMENT);
      flow.goTo(PayStep.CreditDetails);
    }
  };

  const handleConfirmCreditType = () => {
    if (!paymentType) return;
    flow.goTo(PayStep.CreditDetails);
  };

  const handleConfirmCreditDetails = (isCreditsEnough: boolean) => {
    flow.setIsCreditsEnough(isCreditsEnough);
    requestOtp();
  };

  const isSheetOpen = isOpen && step !== PayStep.Otp;
  const showCashBackLoading =
    (isCashBackCardsLoading ||
      isCreditPayAvailableOptionsLoading ||
      isCreditPayAvailableAmountLoading) &&
    (step === PayStep.Method || step === PayStep.CreditType);

  return (
    <>
      <BottomSheet
        open={isSheetOpen}
        onDismiss={flow.close}
        blocking
        className="custom-bottom-sheet"
      >
        <div
          dir="rtl"
          className="relative flex flex-col w-full p-6 CalenderWrapperBottomSheet-wrapper"
        >
          {showCashBackLoading && (
            <div className="absolute inset-0 z-20 flex items-center justify-center bg-white/60 backdrop-blur-sm">
              <SpinnerLoading size={36} />
            </div>
          )}

          <button
            type="button"
            aria-label="بستن"
            className="absolute text-2xl text-gray-500 top-6 left-6"
            onClick={flow.close}
          >
            <i className="icon-nameClose-remove" />
          </button>

          {step === PayStep.Method && (
            <PaymentMethodStep
              isCreditPayEnabled={isCreditPayEnabled}
              onSelect={handleSelectMethod}
            />
          )}

          {step === PayStep.CreditType && (
            <CreditTypeStep
              totalCashBackActiveCredit={totalCashBackActiveCredit ?? 0}
              totalBalanceCredit={creditPayAvailableAmounts?.credit_amount ?? 0}
              value={paymentType}
              isLoading={isCashBackCardsLoading}
              onChange={flow.setPaymentType}
              onConfirm={handleConfirmCreditType}
            />
          )}

          {isOpen && step === PayStep.CreditDetails && (
            <CreditDetailsStep
              transactionType={transactionType}
              paymentType={paymentType}
              orderGuid={orderGuid}
              storeOrderGuid={storeOrderGuid}
              isSubmitting={isRequestingOtp}
              onConfirm={handleConfirmCreditDetails}
              onUnavailable={flow.close}
            />
          )}
        </div>
      </BottomSheet>

      {isOpen && step === PayStep.Otp && (
        <OtpStep
          transactionType={transactionType}
          paymentType={finalPaymentType}
          orderGuid={orderGuid}
          storeOrderGuid={storeOrderGuid}
          onClose={flow.close}
          onInvalidCode={flow.showInvalidCode}
          onInsufficientBalance={() => goToCashPayment(finalPaymentType)}
          onSuccess={goToMyOrders}
        />
      )}

      <ErrorBottomSheet
        isOpen={flow.isInvalidCodeOpen}
        setIsOpen={flow.setIsInvalidCodeOpen}
        title="کد نادرست"
        description="کد وارد شده نادرست است. دوباره تلاش کنید."
      />
    </>
  );
};

export default LendTechPaySheet;
